import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";
import { Wallet, Plus, Trash2, Save, Receipt as ReceiptIcon, AlertTriangle, Lock } from "lucide-react";
import { cn } from "../lib/utils";
import { Receipts, apiErrorMessage } from "../lib/api";
import { fmtDate } from "../lib/auditFormat";
import { useApp } from "../context/AppContext";

/**
 * Payment Receipts editor.
 *
 * Used in two places:
 *  1. SalesForm Step 11 — controlled mode (rows lifted into form state). Cap
 *     is computed locally from the form's payment + buy_mode.
 *  2. SalesDetail — uncontrolled mode (loads from /payment_receipts, saves
 *     via the dedicated endpoint). Editable even after release.
 *
 * The component itself enforces the cap (Total Receipt ≤ cap) and surfaces
 * a friendly warning the moment the total goes over.
 */

const MODES = [
  { value: "cash",          label: "Cash"          },
  { value: "upi",           label: "UPI"           },
  { value: "cheque",        label: "Cheque"        },
  { value: "bank_transfer", label: "Bank Transfer" },
];

const blankRow = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
  receipt_no: "",
  mode: "cash",
  amount: "",
  received_on: new Date().toISOString().slice(0, 10),
  notes: "",
});

function PaymentReceiptsEditor({
  // Controlled mode (Sales Form)
  value, onChange, cap: capProp, buyMode: buyModeProp,
  // Uncontrolled mode (Sale Detail)
  saleId, userId, onSaved,
  // Common
  disabled, title = "Payment Receipts",
  testid = "receipts-editor",
}) {
  const isControlled = Array.isArray(value);
  const { activeUser } = useApp();
  const role = activeUser?.role;
  // Only Admin / Accounts can delete an already-saved receipt. New (unsaved)
  // rows can always be removed by the operator — they don't carry an
  // existing id yet, so no audit / role gate is needed for those.
  const canDeleteSaved = role === "Admin" || role === "Accounts";
  const [serverRows, setServerRows] = useState([]);
  const [serverCap, setServerCap] = useState(0);
  const [buyMode, setBuyMode] = useState("cash");
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmIdx, setConfirmIdx] = useState(null); // index pending delete confirmation

  // Load from server when uncontrolled.
  useEffect(() => {
    if (isControlled || !saleId) return;
    (async () => {
      try {
        const d = await Receipts.list(saleId);
        setServerRows((d.receipts || []).map((r) => ({ ...r, amount: String(r.amount ?? "") })));
        setServerCap(d.cap || 0);
        setBuyMode(d.buy_mode || "cash");
      } catch (e) {
        toast.error(apiErrorMessage(e));
      }
    })();
  }, [isControlled, saleId, reloadKey]);

  const rows = isControlled ? value : serverRows;
  const cap = isControlled ? (Number(capProp) || 0) : serverCap;
  const mode = isControlled ? (buyModeProp || "cash") : buyMode;

  const total = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [rows],
  );
  const over = cap > 0 && total > cap + 0.01;
  const remaining = Math.max(cap - total, 0);
  // Fully-received = cap is defined AND every rupee is captured on saved rows.
  // Blocks "Create Receipt" per spec (§9).
  const fullyReceived = cap > 0 && !over && remaining < 0.5;

  const setRows = (next) => {
    if (isControlled) onChange(next);
    else setServerRows(next);
  };

  const addRow = () => {
    if (disabled) return;
    if (fullyReceived) {
      toast.info("Payment Fully Received.");
      return;
    }
    setRows([...(rows || []), blankRow()]);
  };
  const updateRow = (idx, patch) => {
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
  };

  const onSave = async () => {
    if (isControlled) return;            // parent saves
    if (saving) return;
    if (over) {
      toast.error(
        `Receipt amount cannot exceed the remaining amount receivable from the customer: ₹${remaining.toLocaleString("en-IN")}.`
      );
      return;
    }
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        id: r.id?.startsWith("tmp-") ? undefined : r.id,
        receipt_no: r.receipt_no || null,
        mode: r.mode,
        amount: Number(r.amount) || 0,
        received_on: r.received_on || null,
        notes: r.notes || null,
      }));
      await Receipts.save(saleId, payload, userId);
      toast.success("Payment receipts saved");
      setReloadKey((n) => n + 1);
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const capLabel = "To Receive from Customer";
  // Retained for future use; the totals strip references a literal label.
  void capLabel;

  return (
    <Card className="border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Wallet className="h-4 w-4" />
          </span>
          <div>
            <div className="font-display text-base font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-slate-500">
              Record cash / UPI / cheque / bank transfer receipts collected from the customer.
            </div>
          </div>
        </div>
        {!isControlled && !disabled && (
          <Button onClick={onSave} disabled={saving || over}
            data-testid={`${testid}-save`} className="gap-1.5">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Receipts"}
          </Button>
        )}
      </div>

      {/* Totals strip */}
      <div className="mt-4 grid grid-cols-3 gap-3" data-testid={`${testid}-totals`}>
        <StatBox
          label="To Receive from Customer"
          value={`₹${cap.toLocaleString("en-IN")}`}
          tone="slate"
          testid={`${testid}-cap`}
        />
        <StatBox
          label="Total Received"
          value={`₹${total.toLocaleString("en-IN")}`}
          tone={over ? "rose" : total > 0 ? "emerald" : "slate"}
          testid={`${testid}-received`}
        />
        <StatBox
          label={over ? "Over by" : "Balance Receivable"}
          value={`₹${(over ? total - cap : remaining).toLocaleString("en-IN")}`}
          tone={over ? "rose" : fullyReceived ? "emerald" : "amber"}
          testid={`${testid}-remaining`}
        />
      </div>

      {fullyReceived && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200"
             data-testid={`${testid}-fully-received`}>
          <ReceiptIcon className="h-3.5 w-3.5 flex-shrink-0" />
          Payment Fully Received
        </div>
      )}

      {over && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 ring-1 ring-rose-200"
             data-testid={`${testid}-over-warning`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Receipt amount cannot exceed the remaining amount receivable from
            the customer: ₹{remaining.toLocaleString("en-IN")}. Remove or
            reduce a row to continue.
          </span>
        </div>
      )}

      {/* Rows */}
      <div className="mt-4 space-y-2">
        {rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-3 py-5 text-center text-sm text-slate-500"
               data-testid={`${testid}-empty`}>
            <ReceiptIcon className="mx-auto mb-1 h-5 w-5 text-slate-400" />
            No receipts captured yet.
          </div>
        ) : (
          rows.map((r, i) => (
            <ReceiptRow
              key={r.id || i}
              row={r}
              disabled={disabled}
              onChange={(patch) => updateRow(i, patch)}
              onRemove={() => {
                const isSaved = !!r.id && !String(r.id).startsWith("tmp-");
                if (isSaved && !canDeleteSaved) {
                  // Disabled icon — should not be reachable, but guard anyway.
                  return;
                }
                if (isSaved) {
                  setConfirmIdx(i); // open confirmation popup
                } else {
                  removeRow(i);     // unsaved row — drop silently
                }
              }}
              canDeleteSaved={canDeleteSaved}
              index={i}
              testid={testid}
            />
          ))
        )}
      </div>

      {!disabled && (
        <Button variant="outline" onClick={addRow} className="mt-3 gap-1.5"
                disabled={fullyReceived}
                title={fullyReceived ? "Payment Fully Received" : ""}
                data-testid={`${testid}-add`}>
          <Plus className="h-4 w-4" />
          {fullyReceived ? "Payment Fully Received" : "Add Receipt"}
        </Button>
      )}

      {/* Delete confirmation popup — only opens for already-saved rows */}
      <Dialog open={confirmIdx != null} onOpenChange={(o) => !o && setConfirmIdx(null)}>
        <DialogContent data-testid={`${testid}-delete-confirm`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" /> Delete Receipt?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this receipt?
              This action will be recorded in activity history.
            </DialogDescription>
          </DialogHeader>
          {confirmIdx != null && rows[confirmIdx] && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-slate-500">Receipt No</span>
                <span className="font-mono-data text-slate-900">
                  {rows[confirmIdx].receipt_no || "—"}
                </span>
                <span className="text-slate-500">Amount</span>
                <span className="font-mono-data font-semibold text-slate-900">
                  ₹{Number(rows[confirmIdx].amount || 0).toLocaleString("en-IN")}
                </span>
                <span className="text-slate-500">Mode</span>
                <span className="uppercase text-slate-900">{rows[confirmIdx].mode || "—"}</span>
                <span className="text-slate-500">Date</span>
                <span className="font-mono-data text-slate-900">
                  {rows[confirmIdx].received_on ? fmtDate(rows[confirmIdx].received_on) : "—"}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmIdx(null)}
              data-testid={`${testid}-delete-cancel`}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const idx = confirmIdx;
                setConfirmIdx(null);
                if (idx != null) removeRow(idx);
              }}
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid={`${testid}-delete-confirm-btn`}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ReceiptRow({ row, onChange, onRemove, disabled, canDeleteSaved, index, testid }) {
  const isSaved = !!row.id && !String(row.id).startsWith("tmp-");
  const deleteLocked = isSaved && !canDeleteSaved;
  return (
    <div className="grid grid-cols-12 items-end gap-2 rounded-lg border border-slate-200 bg-white p-3"
         data-testid={`${testid}-row-${index}`}>
      <div className="col-span-12 sm:col-span-3">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Money Receipt No.
        </Label>
        <Input value={row.receipt_no || ""} disabled={disabled}
          onChange={(e) => onChange({ receipt_no: e.target.value })}
          placeholder="Optional"
          data-testid={`${testid}-row-${index}-receipt-no`} />
      </div>
      <div className="col-span-6 sm:col-span-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Mode *</Label>
        <Select value={row.mode || "cash"} onValueChange={(v) => onChange({ mode: v })} disabled={disabled}>
          <SelectTrigger data-testid={`${testid}-row-${index}-mode`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODES.map((m) => (
              <SelectItem key={m.value} value={m.value}
                          data-testid={`${testid}-row-${index}-mode-${m.value}`}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-6 sm:col-span-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Amount (₹) *</Label>
        <Input type="number" min="0" value={row.amount} disabled={disabled}
          onChange={(e) => onChange({ amount: e.target.value })}
          placeholder="0"
          data-testid={`${testid}-row-${index}-amount`} />
      </div>
      <div className="col-span-6 sm:col-span-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Date</Label>
        <Input type="date" value={row.received_on || ""} disabled={disabled}
          onChange={(e) => onChange({ received_on: e.target.value })}
          data-testid={`${testid}-row-${index}-date`} />
        {row.received_on && (
          <div className="mt-0.5 text-[10px] text-slate-400">{fmtDate(row.received_on)}</div>
        )}
      </div>
      <div className="col-span-10 sm:col-span-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes</Label>
        <Input value={row.notes || ""} disabled={disabled}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Optional"
          data-testid={`${testid}-row-${index}-notes`} />
      </div>
      <div className="col-span-2 sm:col-span-1 flex justify-end">
        {!disabled && (
          deleteLocked ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex h-9 w-9 cursor-not-allowed items-center justify-center rounded-md text-slate-300"
                    data-testid={`${testid}-row-${index}-remove-locked`}
                  >
                    <Lock className="h-4 w-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs text-xs">
                  Only Accounts or Admin can delete receipts
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600"
              data-testid={`${testid}-row-${index}-remove`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, tone, testid }) {
  const tones = {
    slate:   "border-slate-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50/50",
    amber:   "border-amber-200 bg-amber-50/50",
    rose:    "border-rose-200 bg-rose-50/50",
  };
  return (
    <div className={cn("rounded-md border px-3 py-2", tones[tone] || tones.slate)} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 font-mono-data text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default PaymentReceiptsEditor;
